/**
 * @file DualCamera.js
 * @description Page component for dual web camera capture interface (customer face photo and check document capture simultaneously).
 */

import '../css/pages/DualCamera.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../components/Layout';

/**
 * Starts video media stream on specified HTMLVideoElement using device ID.
 * @async
 * @param {string} deviceId - Target camera device ID.
 * @param {React.RefObject<HTMLVideoElement>} videoRef - Video element reference.
 * @returns {Promise<void>}
 */
async function startStream(deviceId, videoRef) {
  // Stop any existing stream first
  if (videoRef.current && videoRef.current.srcObject) {
    videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
    videoRef.current.srcObject = null;
  }
  if (!deviceId) return;

  const stream = await navigator.mediaDevices.getUserMedia({
    video: { deviceId: { exact: deviceId } },
    audio: false,
  });

  if (videoRef.current) {
    videoRef.current.srcObject = stream;
  }
}

/**
 * Card component representing a single live camera preview box with controls.
 * @component
 * @param {Object} props
 * @param {string} props.title - Card header title.
 * @param {string} [props.subtitle] - Card header subtitle.
 * @param {React.ReactNode} props.icon - Title icon element.
 * @param {string} props.actionLabel - Capture button text.
 * @param {React.ReactNode} props.actionIcon - Capture button icon.
 * @param {string} [props.btnVariant] - Button style variant.
 * @param {boolean} [props.mirror] - Whether to mirror video stream horizontally.
 * @param {Array<MediaDeviceInfo>} props.cameras - List of enumerated video input devices.
 * @param {string} props.selectedId - Currently selected camera deviceId.
 * @param {Function} props.onSelectChange - Change handler for camera select dropdown.
 * @param {Function} props.onCapture - Callback invoked with captured image data URL.
 */
function CameraCard({
  title,
  subtitle,
  icon,
  actionLabel,
  actionIcon,
  btnVariant = 'primary',
  mirror = false,
  cameras,
  selectedId,
  onSelectChange,
  onCapture,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [snapshot, setSnapshot] = useState(null);
  const [snapTime, setSnapTime] = useState('');
  const [isLive, setIsLive] = useState(false);

  // Start / restart stream whenever selected camera changes
  useEffect(() => {
    setIsLive(false);
    if (!selectedId) return;

    startStream(selectedId, videoRef).catch((err) => {
      console.error('Camera error:', err);
      setIsLive(false);
    });

    const videoEl = videoRef.current;
    // Cleanup on unmount or id change
    return () => {
      if (videoEl && videoEl.srcObject) {
        videoEl.srcObject.getTracks().forEach((t) => t.stop());
      }
    };
  }, [selectedId]);

  const handlePlaying = () => setIsLive(true);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');

    if (mirror) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setSnapshot(dataUrl);
    setSnapTime(new Date().toLocaleTimeString());

    if (onCapture) onCapture(dataUrl);
  }, [mirror, onCapture]);

  return (
    <div className="camera-card">
      {/* Card Header */}
      <div className="camera-card-header">
        <div className="camera-card-title">
          <div className="card-icon">{icon}</div>
          <div>
            <h3>{title}</h3>
            <span>{subtitle}</span>
          </div>
        </div>
        <div className={`status-badge ${isLive ? 'live' : 'offline'}`}>
          <span className="status-dot" />
          {isLive ? 'Live' : 'Offline'}
        </div>
      </div>

      {/* Card Body */}
      <div className="camera-card-body">
        {/* Device selector */}
        <div>
          <div className="device-label">Camera Device</div>
          <select
            className="device-select"
            value={selectedId}
            onChange={(e) => onSelectChange(e.target.value)}
          >
            {cameras.length === 0 && (
              <option value="">No cameras found</option>
            )}
            {cameras.map((cam) => (
              <option key={cam.deviceId} value={cam.deviceId}>
                {cam.label || `Camera ${cam.deviceId.substring(0, 8)}…`}
              </option>
            ))}
          </select>
        </div>

        {/* Video feed */}
        <div className={`video-wrapper${mirror ? ' mirror' : ''}`}>
          {!isLive && (
            <div className="video-placeholder">
              <span className="placeholder-icon">📷</span>
              <span>{selectedId ? 'Starting camera…' : 'No camera selected'}</span>
            </div>
          )}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            onPlaying={handlePlaying}
          />
          {isLive && (
            <div className="video-overlay-badge">
              <span style={{ color: '#22c55e' }}>●</span> Live
            </div>
          )}
        </div>

        {/* Snapshot preview */}
        {snapshot && (
          <div className="snapshot-row">
            <img src={snapshot} alt="snapshot" />
            <div className="snapshot-info">
              <div className="snap-label">✓ Captured</div>
              <div className="snap-time">{snapTime}</div>
            </div>
            <button
              className="snapshot-clear"
              onClick={() => setSnapshot(null)}
              title="Clear snapshot"
            >
              ✕
            </button>
          </div>
        )}

        {/* Action button */}
        <button
          className={`camera-action-btn ${btnVariant}`}
          onClick={handleCapture}
          disabled={!isLive}
        >
          {actionIcon} {actionLabel}
        </button>

        {/* Hidden canvas for snapshot */}
        <canvas ref={canvasRef} className="hidden-canvas" />
      </div>
    </div>
  );
}

/**
 * Main DualCamera page component.
 * @component
 * @returns {React.ReactElement}
 */
export default function DualCamera() {
  const [cameras, setCameras] = useState([]);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [permissionError, setPermissionError] = useState('');

  // Each camera has its own selected deviceId
  const [userCamId, setUserCamId] = useState('');
  const [docCamId, setDocCamId] = useState('');

  // ── Request permission & enumerate devices ─────────────────────────────
  const initCameras = useCallback(async () => {
    // Camera API requires HTTPS (or localhost) — blocked on plain HTTP
    if (!window.isSecureContext) {
      setPermissionError(
        'Camera access requires a secure connection (HTTPS). ' +
        'This page is loaded over HTTP — please enable SSL on your server. ' +
        'Contact your administrator to set up HTTPS (e.g. via Let\'s Encrypt).'
      );
      return;
    }

    try {
      await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      setPermissionGranted(true);
      setPermissionError('');

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === 'videoinput');
      setCameras(videoDevices);

      // Default: first cam for user picture, second (or same) for doc scan
      if (videoDevices.length > 0) setUserCamId(videoDevices[0].deviceId);
      if (videoDevices.length > 1) setDocCamId(videoDevices[1].deviceId);
      else if (videoDevices.length === 1) setDocCamId(videoDevices[0].deviceId);
    } catch (err) {
      setPermissionError(
        err.name === 'NotAllowedError'
          ? 'Camera permission was denied. Please allow camera access in your browser settings.'
          : 'Unable to access cameras. Make sure a camera is connected and try again.'
      );
    }
  }, []);

  useEffect(() => {
    initCameras();
  }, [initCameras]);

  // Callbacks for when a snapshot is taken (extend as needed)
  const handleUserCapture = (dataUrl) => {
    console.log('[DualCamera] User picture captured, length:', dataUrl.length);
    // TODO: pass dataUrl to parent form / API
  };

  const handleDocCapture = (dataUrl) => {
    console.log('[DualCamera] Document scan captured, length:', dataUrl.length);
    // TODO: pass dataUrl to parent form / API
  };

  return (
    <Layout title="Dual Camera">
      <div className="dual-camera-page">

        {/* Intro banner */}
        <div className="page-intro">
          <div className="page-intro-icon">📷</div>
          <div className="page-intro-text">
            <h3>Dual Camera Workspace</h3>
            <p>
              Use two cameras simultaneously — one for the customer photo and one
              for document scanning. Each feed can be captured independently.
            </p>
          </div>
        </div>

        {/* Permission state */}
        {!permissionGranted ? (
          <div className="permission-banner">
            <div className="perm-icon">{!window.isSecureContext ? '🔐' : '🔒'}</div>
            <h2>{!window.isSecureContext ? 'HTTPS Required' : 'Camera Access Required'}</h2>
            <p>
              {permissionError ||
                'This page needs access to your cameras to work.'}
            </p>
            {!window.isSecureContext ? (
              <div style={{
                background: '#fef3cd',
                border: '1px solid #f59e0b',
                borderRadius: 8,
                padding: '10px 16px',
                fontSize: 12.5,
                color: '#92400e',
                marginBottom: 16,
                textAlign: 'left',
                lineHeight: 1.7,
              }}>
                <strong>How to fix on your VPS:</strong><br />
                1. Install Certbot: <code>sudo apt install certbot python3-certbot-nginx</code><br />
                2. Get SSL cert: <code>sudo certbot --nginx -d yourdomain.com</code><br />
                3. Reload Nginx: <code>sudo systemctl reload nginx</code>
              </div>
            ) : (
              <button className="perm-btn" onClick={initCameras}>
                Grant Camera Access
              </button>
            )}
          </div>
        ) : (
          <div className="cameras-grid">
            {/* ── Left: User Picture ── */}
            <CameraCard
              title="User Picture"
              subtitle="Customer / selfie camera"
              icon="🧑"
              actionLabel="Capture Photo"
              actionIcon="📸"
              btnVariant="primary"
              mirror={true}
              cameras={cameras}
              selectedId={userCamId}
              onSelectChange={setUserCamId}
              onCapture={handleUserCapture}
            />

            {/* ── Right: Document Scanner ── */}
            <CameraCard
              title="Document Scanner"
              subtitle="ID / cheque / document camera"
              icon="📄"
              actionLabel="Scan Document"
              actionIcon="🔍"
              btnVariant="secondary"
              mirror={false}
              cameras={cameras}
              selectedId={docCamId}
              onSelectChange={setDocCamId}
              onCapture={handleDocCapture}
            />
          </div>
        )}
      </div>
    </Layout>
  );
}
